import React, { useEffect, useState } from "react";
import { ChevronLeft, Lock, Mail } from "lucide-react";
import { buttonStyles, StyledButton } from "../../../../Shared/button/button";
import ToastSuccess from "../../../(common)/Toast/ToastSuccess";
import ToastWarning from "../../../(common)/Toast/ToastWarning";
import { TEXTS } from "../../constants/texts";
import {
  getPasswordResetRequestErrorMessage,
  sendPasswordResetLink,
} from "../../../../utils/passwordReset";
import { testIdProps } from "../../../../utils/testIdProps";

type ForgotPasswordEmailSentProps = {
  setStep: (step: number) => void;
  email: string;
};

const RESEND_COOLDOWN_SECONDS = 120;

const ForgotPasswordEmailSent: React.FC<ForgotPasswordEmailSentProps> = ({
  setStep,
  email,
}) => {
  const [timer, setTimer] = useState(RESEND_COOLDOWN_SECONDS);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!email) setStep(1);
  }, [email, setStep]);

  useEffect(() => {
    if (timer <= 0) return undefined;

    const interval = window.setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [timer]);

  const formatTime = (seconds: number) => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, "0");
    const remainingSeconds = String(seconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  };

  const handleResend = async () => {
    if (!email || timer > 0 || resending) return;

    try {
      setResending(true);
      await sendPasswordResetLink(email);
      ToastSuccess(email, [
        TEXTS.PASSWORD_RESET_LINK_SENT,
        TEXTS.PASSWORD_RESET_LINK_SENT_MESSAGE,
      ]);
      setTimer(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      ToastWarning("Reset Failed", getPasswordResetRequestErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="container d-flex flex-column shadow bg-white border rounded-4 px-4 py-4"
      style={{
        maxWidth: 400,
        boxShadow:
          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
      }}
    >
      <div className="d-flex align-items-center gap-2 mb-3">
        <ChevronLeft
          {...testIdProps("forgot-password-email-sent-back")}
          className="cursor-pointer text-muted"
          onClick={() => setStep(1)}
        />
        <h2 className="h5 mb-0 fw-bold">Check your email</h2>
      </div>

      <div className="d-flex justify-content-center my-3">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 56, height: 56, background: "#F0FDF4" }}
        >
          <Mail size={24} className="text-success" />
        </div>
      </div>

      <p className="text-muted small mb-1 text-center">
        {TEXTS.PASSWORD_RESET_LINK_SENT_MESSAGE}
      </p>
      <p className="fw-semibold text-center mb-4">{email}</p>

      <p className="text-muted small text-center mb-4">
        Open the link in the email to set a new password. The link expires in 10
        minutes.
      </p>

      <StyledButton
        id="back-to-login"
        type="button"
        className="w-100 mb-3"
        style={buttonStyles.primary.normal}
        hoverStyle={buttonStyles.primary.hover}
        onClick={() => {
          window.location.href = "/login";
        }}
      >
        Back to login
      </StyledButton>

      <div className="text-center small text-muted">
        {timer > 0 ? (
          <span>
            Resend email in{" "}
            <span className="fw-semibold text-dark">{formatTime(timer)}</span>
          </span>
        ) : (
          <button
            {...testIdProps("forgot-password-resend-email")}
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-underline"
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? "Sending..." : "Resend email"}
          </button>
        )}
      </div>

      <div className="text-center small text-muted mt-3 d-flex align-items-center justify-content-center gap-2">
        <Lock size={14} />
        Your data is secure with us
      </div>
    </div>
  );
};

export default ForgotPasswordEmailSent;
